/**
 * Attachment Viewer Modal Component
 * =================================
 * 
 * Comprehensive attachment viewing component that provides file preview, manipulation,
 * and navigation capabilities. This component can be used in two contexts:
 * 1. Form Submission: Within attachment upload section for previewing uploaded files
 * 2. Form Entry View: As a modal when viewing existing form attachments
 * 
 * Core Features:
 * - Multi-format file support (images, videos, PDFs, documents, generic files)
 * - Responsive design with desktop/mobile-specific controls
 * - Advanced image manipulation (zoom, pan, rotation) with touch gesture support
 * - File navigation for multiple attachments
 * - Download and external viewing options
 * - Touch gesture handling with Hammer.js
 * - Keyboard shortcuts for enhanced desktop experience
 * 
 * Supported File Types:
 * - Images: JPEG, PNG, GIF, BMP, TIFF, ICO, WebP with zoom/pan/rotation
 * - Videos: MP4, MOV, WebM, OGG with native video controls
 * - PDFs: Document preview with external viewing options
 * - Documents: Word, TXT, RTF with file information display
 * - Generic Files: Fallback display for unsupported types
 * 
 * Technical Implementation:
 * - Hammer.js integration for touch gestures and multi-touch support
 * - Responsive design with device-specific behavior
 * - File type detection and content adaptation
 * - Transform-based image manipulation
 * - Service integration for attachment management
 * 
 * @author MacSafety Development Team
 * @version 1.0
 */

import { Component, Input, Output, EventEmitter, OnChanges, OnDestroy, SimpleChanges, HostListener, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { ActionSheetController, ToastController } from '@ionic/angular';
import * as Hammer from 'hammerjs';
import { AttachmentUploadService } from '../../../../../services/attachment-upload.service';
import { AttachmentUploadData } from '../../../../../models/forms/attachments/attachment-upload';

import { ModalAttachments } from '../../../../../models/modal-viewer-attachments';
import { ScreenSizeService } from '../../../../../services/screen-size.service'; 

@Component({
  selector: 'app-attachment-viewer-modal',
  templateUrl: './attachment-viewer-modal.component.html',
  styleUrls: ['./attachment-viewer-modal.component.css']
})
export class AttachmentViewerModalComponent implements OnChanges, AfterViewInit, OnDestroy {
  @ViewChild('attachmentViewer', { static: false }) attachmentViewer!: ElementRef;
  @ViewChild('attachmentImage', { static: false }) attachmentImage!: ElementRef;
  private hammer: HammerManager | null = null;

  // Service-based mode (existing)
  @Input() identifier: string = '';

  // Direct input mode (new) - optional array of attachments
  @Input() attachments?: ModalAttachments[];

  // Common inputs (unchanged)
  @Input() isOpen: boolean = false;
  @Input() initialIndex: number = 0;
  @Output() closeModal = new EventEmitter<void>();

  public currentAttachmentIndex: number = 0;
  public selectedAttachment: ModalAttachments | null = null;
  public attachmentList: ModalAttachments[] = [];
  public videoError: boolean = false;
  
  // Zoom, pan, and rotation properties (only for images)
  public scale: number = 1;
  public posX: number = 0;
  public posY: number = 0;
  public rotation: number = 0;
  public startScale: number = 1;
  public startPosX: number = 0;
  public startPosY: number = 0;

  // Mouse wheel zoom properties
  public zoomSpeed: number = 0.1;
  public minScale: number = 0.5;
  public maxScale: number = 5;

  // Touch gesture properties
  private lastTouchDistance: number = 0;
  private isPanning: boolean = false;
  private isPinching: boolean = false;
  public isGesturing: boolean = false;

  constructor(
    private attachmentService: AttachmentUploadService,
    private actionSheetController: ActionSheetController,
    private toastController: ToastController,

    public screenSizeService: ScreenSizeService
  ) {}

  /**
   * Lifecycle hook that is called after the view has been initialized.
   * Sets up Hammer.js touch gestures for mobile image manipulation.
   */
  ngAfterViewInit() {
    this.setupHammerGestures();
  }

  /**
   * Lifecycle hook that is called when the component is about to be destroyed.
   * Cleans up Hammer.js instance to prevent memory leaks.
   */
  ngOnDestroy() {
    if (this.hammer) {
      this.hammer.destroy();
    }
  }

  /**
   * Lifecycle hook that responds to input property changes.
   * Initializes attachments when the modal opens or attachment data changes.
   * 
   * @param changes - Object containing information about changed properties
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']?.currentValue || changes['identifier'] || changes['attachments'] || changes['initialIndex']) {
      this.setInitialAttachment();
    }
  }

  /**
   * Determines if the given attachment is an image file.
   * Checks both MIME type and file extension for comprehensive detection.
   * 
   * @param attachment - The attachment to check
   * @returns True if the attachment is an image, false otherwise
   */
  isImage(attachment: ModalAttachments): boolean {
    return attachment?.type?.startsWith('image/') || 
           attachment?.name?.toLowerCase().match(/\.(jpg|jpeg|png|gif|bmp|tiff|ico|webp)$/) !== null || false;
  }

  /**
   * Determines if the given attachment is a video file.
   * Checks both MIME type and file extension for comprehensive detection.
   * 
   * @param attachment - The attachment to check
   * @returns True if the attachment is a video, false otherwise
   */
  isVideo(attachment: ModalAttachments): boolean {
    return attachment?.type?.startsWith('video/') || 
           attachment?.name?.toLowerCase().match(/\.(mp4|mov|webm|ogg)$/) !== null || false;
  }

  /**
   * Determines if the given attachment is a PDF file.
   * Checks both MIME type and file extension for comprehensive detection.
   * 
   * @param attachment - The attachment to check
   * @returns True if the attachment is a PDF, false otherwise
   */
  isPdf(attachment: ModalAttachments): boolean {
    return attachment?.type === 'application/pdf' || 
           attachment?.name?.toLowerCase().endsWith('.pdf') || false;
  }

  /**
   * Determines if the given attachment is a document file.
   * Supports Word documents, text files, and RTF files.
   * 
   * @param attachment - The attachment to check
   * @returns True if the attachment is a document, false otherwise
   */
  isDocument(attachment: ModalAttachments): boolean {
    const documentTypes = [
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
      'text/rtf'
    ];
    return documentTypes.includes(attachment?.type || '') || 
           Boolean(attachment?.name?.toLowerCase().match(/\.(doc|docx|txt|rtf)$/));
  }

  /**
   * Gets the appropriate icon for the given attachment type.
   * Creates a temporary AttachmentUploadData object to use the service method.
   * 
   * @param attachment - The attachment to get the icon for
   * @returns String representing the icon name
   */
  getFileIcon(attachment: ModalAttachments): string {
    // Create a temporary AttachmentUploadData object for the service call
    const tempAttachment: AttachmentUploadData = {
      id: '',
      name: attachment.name,
      type: attachment.type,
      size: attachment.size,
      url: attachment.url,
      uploadDate: new Date()
    };
    return this.attachmentService.getFileIcon(tempAttachment);
  }

  /**
   * Gets a human-readable description of the file type.
   * Returns appropriate labels for images, videos, PDFs, documents, and generic files.
   * 
   * @param attachment - The attachment to get the description for
   * @returns String describing the file type
   */
  getFileTypeDescription(attachment: ModalAttachments): string {
    if (this.isImage(attachment)) return 'Image';
    if (this.isVideo(attachment)) return 'Video';
    if (this.isPdf(attachment)) return 'PDF Document';
    if (this.isDocument(attachment)) return 'Document';
    return 'File';
  }

  /**
   * Formats file size from bytes to human-readable format.
   * Converts bytes to appropriate units (B, KB, MB, GB) with one decimal place.
   * 
   * @param bytes - The file size in bytes
   * @returns Formatted file size string
   */
  formatFileSize(bytes: any): string {
    if (typeof bytes === 'string') {
      return bytes;
    }
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  /**
   * Initializes the attachment list and selected attachment.
   * Handles both direct attachment input and service-based loading modes.
   * Resets transform properties and sets the initial attachment index.
   * 
   * @private
   */
  private setInitialAttachment(): void {
    // Check if we have direct attachments input
    if (this.attachments && this.attachments.length > 0) {
      this.attachmentList = [...this.attachments]; // Direct copy, already ModalAttachments
    } else if (this.identifier) {
      // Service-based loading - convert from AttachmentUploadData to ModalAttachments
      const formUploadAttachments = this.attachmentService.getAttachments(this.identifier);
      this.attachmentList = ModalAttachments.fromAttachmentList(formUploadAttachments);
    } else {
      this.attachmentList = [];
    }

    this.currentAttachmentIndex = this.initialIndex;
    
    if (this.attachmentList && this.attachmentList.length > 0) {
      if (this.currentAttachmentIndex >= this.attachmentList.length) {
        this.currentAttachmentIndex = 0;
      }
      this.selectedAttachment = this.attachmentList[this.currentAttachmentIndex];
      this.resetTransform();
    } else {
      this.selectedAttachment = null;
    }
  }

  /**
   * Navigates to the next or previous attachment in the list.
   * Updates the current index, selected attachment, and resets transform properties.
   * Resets video error state when navigating to a new video.
   * 
   * @param direction - Direction to navigate (-1 for previous, 1 for next)
   */
  navigateAttachment(direction: number): void {
    const newIndex = this.currentAttachmentIndex + direction;
    if (newIndex >= 0 && newIndex < this.attachmentList.length) {
      this.currentAttachmentIndex = newIndex;
      this.selectedAttachment = this.attachmentList[this.currentAttachmentIndex];
      this.resetTransform();
      this.videoError = false; // Reset video error state
    }
  }

  /**
   * Handles video playback errors.
   * Sets the videoError flag to true when a video cannot be played.
   */
  onVideoError(): void {
    this.videoError = true;
  }

  /**
   * Emits the closeModal event to notify the parent component.
   * Used to close the modal from within the component.
   */
  onCloseModal(): void {
    this.closeModal.emit();
  }

  /**
   * Rotates the currently selected image by 90 degrees clockwise.
   * Only affects image attachments and maintains rotation state.
   */
  rotateImage(): void {
    if (this.selectedAttachment && this.isImage(this.selectedAttachment)) {
      this.rotation = (this.rotation + 90) % 360;
    }
  }

  /**
   * Resets all transform properties to their default values.
   * Resets scale, position, rotation, and starting values for gestures.
   */
  resetTransform(): void {
    this.scale = 1;
    this.posX = 0;
    this.posY = 0;
    this.rotation = 0;
    this.startScale = 1;
    this.startPosX = 0;
    this.startPosY = 0;
  }

  /**
   * Resets only zoom and pan properties while preserving rotation.
   * Useful for maintaining image orientation while resetting view position.
   */
  resetZoom(): void {
    // Separate method to just reset zoom/pan but keep rotation
    this.scale = 1;
    this.posX = 0;
    this.posY = 0;
  }

  /**
   * Toggles between zoomed and normal view for images.
   * Zooms to 2x at the click/tap point or resets to normal view.
   * Supports both mouse and touch events.
   * 
   * @param event - Mouse or touch event that triggered the zoom
   */
  toggleZoom(event: MouseEvent | TouchEvent): void {
    if (this.selectedAttachment && this.isImage(this.selectedAttachment)) {
      if (this.scale === 1) {
        // Zoom to 2x at the click/tap point
        const rect = this.attachmentViewer.nativeElement.getBoundingClientRect();
        let clientX: number, clientY: number;
        
        if (event instanceof MouseEvent) {
          clientX = event.clientX;
          clientY = event.clientY;
        } else {
          clientX = event.changedTouches[0].clientX;
          clientY = event.changedTouches[0].clientY;
        }
        
        const x = clientX - rect.left - rect.width / 2;
        const y = clientY - rect.top - rect.height / 2;
        
        this.scale = 2;
        this.posX = -x;
        this.posY = -y;
      } else {
        this.resetZoom();
      }
    }
  }

  /**
   * Increases the zoom level of the current image.
   * Respects the maximum zoom limit and only affects image attachments.
   */
  zoomIn(): void {
    if (this.selectedAttachment && this.isImage(this.selectedAttachment) && this.scale < this.maxScale) {
      this.scale = Math.min(this.maxScale, this.scale + 0.5);
    }
  }

  /**
   * Decreases the zoom level of the current image.
   * Respects the minimum zoom limit and only affects image attachments.
   */
  zoomOut(): void {
    if (this.selectedAttachment && this.isImage(this.selectedAttachment) && this.scale > this.minScale) {
      this.scale = Math.max(this.minScale, this.scale - 0.5);
    }
  }

  /**
   * Generates the CSS transform string for image manipulation.
   * Combines scale, translation, and rotation transforms.
   * 
   * @returns CSS transform string for the image element
   */
  getImageTransform(): string {
    return `scale(${this.scale}) translate(${this.posX}px, ${this.posY}px) rotate(${this.rotation}deg)`;
  }

  /**
   * Presents document options in an action sheet for mobile devices.
   * Provides options to open in new tab, download, or cancel.
   * Only available for PDF and document attachments.
   */
  async presentDocumentOptions() {
    if (!this.selectedAttachment) return;

    const actionSheet = await this.actionSheetController.create({
      header: 'Document Options',
      buttons: [
        {
          text: 'Open in New Tab',
          icon: 'open-outline',
          handler: () => {
            this.openInNewTab();
          }
        },
        {
          text: 'Download',
          icon: 'download-outline', 
          handler: () => {
            this.downloadFile();
          }
        },
        {
          text: 'Cancel',
          icon: 'close-outline',
          role: 'cancel'
        }
      ]
    });

    await actionSheet.present();
  }

  /**
   * Opens the current attachment in a new browser tab.
   * Handles both data URLs and regular URLs (like SAS URLs).
   * Converts data URLs to blobs for proper display.
   */
  async openInNewTab(): Promise<void> {
    if (!this.selectedAttachment?.url) return;

    try {
      // Check if it's a data URL
      if (this.selectedAttachment.url.startsWith('data:')) {
        this.openDataUrlAsBlob(this.selectedAttachment.url);
      } else {
        // For regular URLs (like SAS URLs)
        window.open(this.selectedAttachment.url, '_blank');
      }
    } catch (error) {
      console.error('Error opening attachment in new tab', error);
    }
  }

  /**
   * Converts a data URL to a blob and opens it in a new tab.
   * Extracts MIME type and base64 data, creates a blob, and opens it.
   * Automatically cleans up the blob URL after opening.
   * 
   * @param dataUrl - The data URL to convert and open
   * @private
   */
  private openDataUrlAsBlob(dataUrl: string): void {
    // Extract the base64 part and mime type
    const [header, base64Data] = dataUrl.split(',');
    const mimeType = header.match(/data:([^;]+)/)?.[1] || 'application/octet-stream';
    
    // Convert to blob
    const binaryString = atob(base64Data);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    
    const blob = new Blob([bytes], { type: mimeType });
    const blobUrl = URL.createObjectURL(blob);
    
    // Open in new tab
    window.open(blobUrl, '_blank');
    
    // Clean up
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  }

  /**
   * Downloads the current attachment to the user's device.
   * Fetches the file, creates a blob, and triggers the download.
   * Shows success or error toast notifications.
   */
  async downloadFile(): Promise<void> {
    if (!this.selectedAttachment) return;

    try {
      const response = await fetch(this.selectedAttachment.url);
      const blob = await response.blob();
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = this.selectedAttachment.name || 'download';
      
      document.body.appendChild(link);
      link.click();
      
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      const toast = await this.toastController.create({
        message: 'Download started',
        duration: 2000,
        position: 'bottom',
        icon: 'download-outline'
      });
      await toast.present();
    } catch (error) {
      const toast = await this.toastController.create({
        message: 'Download failed',
        duration: 2000,
        position: 'bottom',
        icon: 'alert-circle-outline',
        color: 'danger'
      });
      await toast.present();
    }
  }

  /**
   * Sets up Hammer.js touch gestures for mobile image manipulation.
   * Configures pinch, pan, and tap gestures with device-specific behavior.
   * Handles touch action management for optimal mobile experience.
   * 
   * @private
   */
  private setupHammerGestures(): void {
    if (!this.attachmentViewer || !this.attachmentViewer.nativeElement) return;

    // Configure touch behavior based on device type
    if (this.screenSizeService.isMobileDevice()) {
      // Mobile: Allow touch actions for native scrolling on non-image content
      this.attachmentViewer.nativeElement.style.touchAction = 'auto';
    } else {
      // Desktop: Disable touch actions for better gesture control
      this.attachmentViewer.nativeElement.style.touchAction = 'none';
    }
    
    this.attachmentViewer.nativeElement.style.userSelect = 'none';

    this.hammer = new Hammer.Manager(this.attachmentViewer.nativeElement);
    
    const pinch = new Hammer.Pinch();
    const pan = new Hammer.Pan();
    const doubleTap = new Hammer.Tap({ event: 'doubletap', taps: 2 });
    const singleTap = new Hammer.Tap({ event: 'singletap' });
    
    // Configure recognizers
    pinch.set({ enable: true });
    pan.set({ direction: Hammer.DIRECTION_ALL, threshold: 10 });
    
    this.hammer.add([pinch, pan, doubleTap, singleTap]);
    
    // Enable simultaneous recognition
    this.hammer.get('pinch').recognizeWith('pan');
    this.hammer.get('doubletap').recognizeWith('singletap');
    this.hammer.get('singletap').requireFailure('doubletap');

    // Pinch handlers
    this.hammer.on('pinchstart', (e) => {
      if (this.selectedAttachment && this.isImage(this.selectedAttachment)) {
        this.isPinching = true;
        this.isGesturing = true;
        this.startScale = this.scale;
        
        // Disable touch actions during pinch on mobile
        if (this.screenSizeService.isMobileDevice()) {
          this.attachmentViewer.nativeElement.style.touchAction = 'none';
        }
      }
    });

    this.hammer.on('pinch', (e) => {
      e.preventDefault();
      if (this.selectedAttachment && this.isImage(this.selectedAttachment) && this.isPinching) {
        const newScale = Math.max(this.minScale, Math.min(this.maxScale, this.startScale * e.scale));
        
        // Calculate focal point for pinch zoom
        const rect = this.attachmentViewer.nativeElement.getBoundingClientRect();
        const centerX = e.center.x - rect.left - rect.width / 2;
        const centerY = e.center.y - rect.top - rect.height / 2;
        
        // Adjust position to keep the pinch center stationary
        const scaleDiff = newScale / this.scale;
        this.posX = this.posX * scaleDiff - centerX * (scaleDiff - 1);
        this.posY = this.posY * scaleDiff - centerY * (scaleDiff - 1);
        
        this.scale = newScale;
      }
    });

    this.hammer.on('pinchend', () => {
      this.isPinching = false;
      this.isGesturing = false;
      this.startScale = this.scale;
      
      // Re-enable touch actions on mobile after pinch
      if (this.screenSizeService.isMobileDevice()) {
        this.attachmentViewer.nativeElement.style.touchAction = 'auto';
      }
    });

    // Pan handlers
    this.hammer.on('panstart', (e) => {
      if (this.selectedAttachment && this.isImage(this.selectedAttachment)) {
        // Only allow panning when zoomed in or on desktop
        if (this.scale > 1 || !this.screenSizeService.isMobileDevice()) {
          this.isPanning = true;
          this.isGesturing = true;
          this.startPosX = this.posX;
          this.startPosY = this.posY;
          
          // Disable touch actions during pan
          if (this.screenSizeService.isMobileDevice()) {
            this.attachmentViewer.nativeElement.style.touchAction = 'none';
          }
        }
      }
    });

    this.hammer.on('pan', (e) => {
      e.preventDefault();
      if (this.selectedAttachment && this.isImage(this.selectedAttachment) && this.isPanning) {
        const sensitivity = this.screenSizeService.isMobileDevice() ? 1 : Math.max(0.5, 1 / this.scale);
        
        // Don't apply boundaries - let the image move freely when zoomed
        this.posX = this.startPosX + (e.deltaX * sensitivity);
        this.posY = this.startPosY + (e.deltaY * sensitivity);
      }
    });

    this.hammer.on('panend', () => {
      this.isPanning = false;
      this.isGesturing = false;
      
      // Re-enable touch actions on mobile after pan
      if (this.screenSizeService.isMobileDevice()) {
        this.attachmentViewer.nativeElement.style.touchAction = 'auto';
      }
    });

    // Double tap to zoom
    this.hammer.on('doubletap', (e) => {
      if (this.selectedAttachment && this.isImage(this.selectedAttachment)) {
        this.toggleZoom(e.srcEvent as TouchEvent);
      }
    });

    // Single tap handler for mobile navigation
    if (this.screenSizeService.isMobileDevice()) {
      this.hammer.on('singletap', (e) => {
        if (this.attachmentList.length > 1) {
          const rect = this.attachmentViewer.nativeElement.getBoundingClientRect();
          const tapX = e.center.x - rect.left;
          const threshold = rect.width * 0.3; // 30% from edges
          
          if (tapX < threshold) {
            this.navigateAttachment(-1);
          } else if (tapX > rect.width - threshold) {
            this.navigateAttachment(1);
          }
        }
      });
    }
  }

  /**
   * Handles mouse wheel events for desktop zoom functionality.
   * Provides smooth zoom in/out with mouse wheel on desktop devices.
   * Zooms toward the mouse cursor position for intuitive interaction.
   * 
   * @param event - Wheel event from the browser
   */
  @HostListener('wheel', ['$event'])
  onWheel(event: WheelEvent): void {
    // Only handle wheel events on desktop devices
    if (this.screenSizeService.isMobileDevice()) {
      return;
    }
    
    if (this.isOpen && this.selectedAttachment && this.isImage(this.selectedAttachment)) {
      event.preventDefault();
      
      const delta = event.deltaY > 0 ? -this.zoomSpeed : this.zoomSpeed;
      const newScale = Math.max(this.minScale, Math.min(this.maxScale, this.scale + delta));
      
      if (newScale !== this.scale) {
        const rect = this.attachmentViewer.nativeElement.getBoundingClientRect();
        const mouseX = event.clientX - rect.left;
        const mouseY = event.clientY - rect.top;
        
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        
        const distX = (mouseX - centerX) / this.scale;
        const distY = (mouseY - centerY) / this.scale;
        
        const offsetX = distX * (1 - newScale / this.scale);
        const offsetY = distY * (1 - newScale / this.scale);
        
        this.scale = newScale;
        this.posX += offsetX;
        this.posY += offsetY;
        this.startScale = this.scale;
      }
    }
  }

  /**
   * Handles keyboard events for enhanced desktop experience.
   * Provides keyboard shortcuts for navigation, zoom, rotation, and modal control.
   * Only active when the modal is open and an attachment is selected.
   * 
   * @param event - Keyboard event from the browser
   */
  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (this.isOpen && this.selectedAttachment) {
      if (event.key === 'ArrowLeft') {
        this.navigateAttachment(-1);
      } else if (event.key === 'ArrowRight') {
        this.navigateAttachment(1);
      } else if (event.key === 'Escape') {
        this.onCloseModal();
      } else if (this.isImage(this.selectedAttachment)) {
        // Image-specific controls
        if (event.key === '+' || event.key === '=') {
          this.zoomIn();
        } else if (event.key === '-') {
          this.zoomOut();
        } else if (event.key === '0') {
          this.resetTransform();
        } else if (event.key === 'r' || event.key === 'R') {
          this.rotateImage();
        }
      }
    }
  }
}